import React, { useState } from "react";
import { KeyRound } from "lucide-react";
import {
  PromptOverlay,
  PromptContent,
  IconWrapper,
  PromptTitle,
  PromptDescription,
  ButtonGroup,
  PromptButton,
  StatusMessage,
  Spinner,
} from "./index.styled";
import {
  passkeyRegisterBegin,
  passkeyRegisterFinish,
  dismissPasskeyPrompt,
  isPasskeyPromptDismissed,
  isPasskeyLoginSource,
} from "../../../utils/apiService";
import {
  prepareCredentialCreationOptions,
  serializeCredential,
} from "../../../utils/webauthn";

interface PasskeyPromptProps {
  isVisible: boolean;
  onDone: () => void;
}

const PasskeyPrompt: React.FC<PasskeyPromptProps> = ({
  isVisible,
  onDone,
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isVisible) return null;

  const handleDismiss = () => {
    dismissPasskeyPrompt();
    onDone();
  };

  const handleRegister = async () => {
    setError(null);
    setLoading(true);

    try {
      // Step 1: Get registration options from server
      const options = await passkeyRegisterBegin();

      // Step 2: Ask browser to create credential
      const optionsInput = (options.publicKey || options) as unknown as Parameters<typeof prepareCredentialCreationOptions>[0];
      const publicKeyOptions = prepareCredentialCreationOptions(optionsInput);
      const credential = (await navigator.credentials.create({
        publicKey: publicKeyOptions,
      })) as PublicKeyCredential | null;

      if (!credential) {
        setError("הפעולה בוטלה");
        setLoading(false);
        return;
      }

      // Step 3: Send credential to server
      const serialized = serializeCredential(credential);
      await passkeyRegisterFinish(serialized);

      // Step 4: Success
      setSuccess(true);
      dismissPasskeyPrompt();
      setTimeout(() => onDone(), 1500);
    } catch (err) {
      console.error("Passkey registration error:", err);
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        setError("הפעולה בוטלה");
      } else {
        setError("הרישום נכשל. נסה שוב מאוחר יותר.");
      }
      setLoading(false);
    }
  };

  return (
    <PromptOverlay onClick={handleDismiss}>
      <PromptContent onClick={(e) => e.stopPropagation()}>
        <IconWrapper>
          <KeyRound size={24} color="white" />
        </IconWrapper>

        <PromptTitle>רוצה להתחבר בקלות בפעם הבאה?</PromptTitle>
        <PromptDescription>
          הוסף מפתח גישה כדי להתחבר בלחיצה אחת, בלי לעבור דרך הבוט.
        </PromptDescription>

        {!success && (
          <ButtonGroup>
            <PromptButton
              $variant="secondary"
              onClick={handleDismiss}
              disabled={loading}
            >
              לא עכשיו
            </PromptButton>
            <PromptButton
              $variant="primary"
              onClick={handleRegister}
              disabled={loading}
            >
              {loading ? <Spinner /> : "הוסף מפתח גישה"}
            </PromptButton>
          </ButtonGroup>
        )}

        {success && (
          <StatusMessage $type="success">
            מפתח הגישה נרשם בהצלחה!
          </StatusMessage>
        )}

        {error && <StatusMessage $type="error">{error}</StatusMessage>}
      </PromptContent>
    </PromptOverlay>
  );
};

// Helper to check if the passkey prompt should be shown
// eslint-disable-next-line react-refresh/only-export-components
export const shouldShowPasskeyPrompt = (): boolean => {
  if (isPasskeyPromptDismissed()) return false;
  if (isPasskeyLoginSource()) return false;
  return true;
};

export default PasskeyPrompt;
